/**
 * COOP-safe popup authentication.
 *
 * ── Why this exists ────────────────────────────────────────────────
 * Microsoft's identity endpoints (login.microsoftonline.com /
 * login.windows.net) send `Cross-Origin-Opener-Policy: same-origin`. When the
 * popup navigates there the browser performs a browsing-context-group swap,
 * which sets `window.opener` to null **permanently** — it does not come back
 * when the popup returns to our own origin. Every mechanism that depends on a
 * window reference dies with it:
 *
 *   - window.opener.postMessage        → opener is null
 *   - popup.closed / popup.location    → reports closed immediately
 *   - BroadcastChannel, localStorage,  → the CTI app is a third-party iframe
 *     SharedWorker, ServiceWorker         inside Salesforce, so its storage is
 *                                         partitioned under salesforce.com
 *                                         while the popup is top-level and
 *                                         unpartitioned. Different partitions
 *                                         never see each other's messages.
 *
 * The only channel immune to both COOP and storage partitioning is the server.
 * So: the opener mints a random flowId, passes it to /login, and polls
 * /api/auth-flow/:flowId until the popup's /redirect publishes the result.
 *
 * postMessage is kept as a fast path. When COOP happens not to fire (older
 * browsers, or if Microsoft rolls the header back again) the login completes
 * instantly instead of waiting for the next poll tick. Whichever path wins
 * first settles the promise; the other is cancelled.
 *
 * Verified empirically against a live Salesforce org: BroadcastChannel
 * round-trips between a top-level page and its popup, and delivers nothing
 * at all from inside the Salesforce iframe. Hence the server rendezvous.
 */

const POLL_INTERVAL_MS = 2000;
const DEFAULT_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * How long to wait before deciding whether `popup.closed` can be believed.
 *
 * Under COOP the browsing-context-group swap severs our handle to the popup
 * and `popup.closed` starts reporting true within about a second of the
 * navigation to Microsoft. Trusting it blindly
 * would abort every login almost immediately.
 *
 * So we sample it once after this delay. Still false means the reference
 * survived the navigation, COOP did not fire, and `closed` is meaningful from
 * then on. Already true means COOP severed it and we must never look again.
 * Nobody completes a sign-in and closes the window inside this window.
 */
const CLOSE_TRUST_GRACE_MS = 6000;

/** Errors that mean "the user didn't finish", not "something broke". */
export function isAbandonedAuth(error) {
  return (
    error?.code === "AUTH_TIMEOUT" ||
    error?.code === "AUTH_SUPERSEDED" ||
    error?.code === "AUTH_POPUP_CLOSED"
  );
}

/**
 * The flow currently in progress, if any. Starting a new one cancels it.
 * Without this, an abandoned attempt keeps polling until its timeout and can
 * resolve later, handing a stale transfer token to a call site that has
 * already logged in via a subsequent attempt.
 */
let activeFlow = null;

/** 256-bit random hex — must match FLOW_ID_PATTERN on the server. */
export function createFlowId() {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

function openCentredPopup(url, name) {
  const w = 480;
  const h = 640;
  const left = Math.round((screen.width - w) / 2);
  const top = Math.round((screen.height - h) / 2);
  return window.open(
    url,
    name,
    `popup=yes,width=${w},height=${h},left=${left},top=${top}`,
  );
}

/**
 * Open the auth popup and resolve once the flow completes.
 *
 * @param {object}  options
 * @param {string}  options.loginPath      e.g. "/login" or "/login?target=admin"
 * @param {string}  options.windowName     window.open target name
 * @param {string}  options.successType    postMessage type for the fast path
 * @param {string}  [options.errorType]    postMessage type for the fast path
 * @param {number}  [options.timeoutMs]
 * @returns {Promise<{transferToken: string}>} rejects on error/timeout
 */
export function loginWithPopup({
  loginPath,
  windowName,
  successType,
  errorType,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}) {
  const flowId = createFlowId();
  const separator = loginPath.includes("?") ? "&" : "?";
  const url = `${loginPath}${separator}flowId=${flowId}`;
  const startedAt = Date.now();

  // Cancel anything still running from a previous attempt before starting.
  if (activeFlow) activeFlow.abort();

  return new Promise((resolve, reject) => {
    let settled = false;
    let pollTimer = null;
    let timeoutTimer = null;
    let closeTimer = null;
    let trustGraceTimer = null;
    let closeWatchTrusted = false;

    const cleanup = () => {
      window.removeEventListener("message", onMessage);
      document.removeEventListener("visibilitychange", onAttentionReturned);
      window.removeEventListener("focus", onAttentionReturned);
      if (pollTimer) clearInterval(pollTimer);
      if (closeTimer) clearInterval(closeTimer);
      if (trustGraceTimer) clearTimeout(trustGraceTimer);
      if (timeoutTimer) clearTimeout(timeoutTimer);
      if (activeFlow && activeFlow.flowId === flowId) activeFlow = null;
    };

    console.log("[auth-debug] flowId minted:", flowId);
    console.log("[auth-debug] popup url:", url);

    const settleOk = (transferToken, via) => {
      if (settled) return;
      settled = true;
      console.log(`[auth-debug] SETTLED OK via ${via} after ${Date.now() - startedAt}ms`);
      cleanup();
      resolve({ transferToken });
    };

    const settleErr = (error, via) => {
      if (settled) return;
      settled = true;
      console.log(`[auth-debug] SETTLED ERR via ${via}:`, error.code, error.message);
      cleanup();
      reject(error);
    };

    // ── Fast path: postMessage. A no-op whenever COOP has fired. ──
    function onMessage(event) {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      console.log("[auth-debug] postMessage received:", data.type);

      if (data.type === successType && data.transferToken) {
        settleOk(data.transferToken, "postMessage");
      } else if (errorType && data.type === errorType) {
        const error = new Error(data.message || "Authentication failed.");
        error.code = data.code || "AUTH_FAILED";
        settleErr(error, "postMessage");
      }
    }
    // window.addEventListener("message", onMessage);

    // ── Primary path: server-side rendezvous ──
    let pollCount = 0;
    const poll = async () => {
      if (settled) return;
      const n = ++pollCount;
      try {
        const response = await fetch(`/api/auth-flow/${flowId}`, {
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        if (!response.ok) {
          console.log(`[auth-debug] poll #${n} HTTP ${response.status}, retrying`);
          return; // transient — keep polling
        }

        const result = await response.json();
        console.log(`[auth-debug] poll #${n} →`, result.status, result.transferToken ? "(token present)" : "");

        if (result.status === "complete" && result.transferToken) {
          settleOk(result.transferToken, "poll");
        } else if (result.status === "error") {
          const error = new Error(result.message || "Authentication failed.");
          error.code = result.code || "AUTH_FAILED";
          settleErr(error, "poll");
        }
        // status === "pending" → keep polling
      } catch (e) {
        console.log(`[auth-debug] poll #${n} threw:`, e.message);
        // Network blip: keep polling until the timeout.
      }
    };

    /**
     * Opportunistic extra poll when the user's attention comes back to us,
     * so a completed login is picked up sooner than the next interval tick.
     *
     * Treat this as a bonus, not the mechanism. `visibilitychange` only fires
     * when the document actually becomes hidden (tab switch, minimise), and
     * opening a popup usually does not hide us. `focus` covers more cases but
     * a cross-origin iframe may only receive it when the iframe itself is
     * clicked. The interval above is what actually guarantees delivery.
     */
    const onAttentionReturned = () => {
      if (settled || document.visibilityState === "hidden") return;
      poll();
    };
    document.addEventListener("visibilitychange", onAttentionReturned);
    window.addEventListener("focus", onAttentionReturned);

    const popup = openCentredPopup(url, windowName);
    if (!popup) {
      settleErr(
        Object.assign(new Error("Popup was blocked by the browser."), {
          code: "POPUP_BLOCKED",
        }),
        "popup blocked",
      );
      return;
    }
    console.log("[auth-debug] popup opened, polling every", POLL_INTERVAL_MS, "ms");

    activeFlow = {
      flowId,
      abort: () =>
        settleErr(
          Object.assign(new Error("Superseded by a newer sign-in attempt."), {
            code: "AUTH_SUPERSEDED",
          }),
          "superseded",
        ),
    };

    // Decide once whether popup.closed can be believed for this flow.
    trustGraceTimer = setTimeout(() => {
      if (settled) return;
      let stillOpen = false;
      try {
        stillOpen = !popup.closed;
      } catch (e) {
        /* handle unusable, leave untrusted */
      }
      closeWatchTrusted = stillOpen;
      console.log(
        "[auth-debug] popup.closed trustworthy:",
        closeWatchTrusted,
        closeWatchTrusted ? "(COOP not enforced)" : "(reference severed, will rely on timeout)",
      );
    }, CLOSE_TRUST_GRACE_MS);

    closeTimer = setInterval(async () => {
      if (settled || !closeWatchTrusted) return;
      let closed = false;
      try {
        closed = popup.closed;
      } catch (e) {
        return;
      }
      if (!closed) return;

      // The popup may have closed itself immediately after a successful auth,
      // so give the result one last chance to arrive before giving up.
      console.log("[auth-debug] popup closed, doing a final poll");
      await poll();
      settleErr(
        Object.assign(new Error("Sign-in window was closed."), {
          code: "AUTH_POPUP_CLOSED",
        }),
        "popup closed",
      );
    }, 500);

    pollTimer = setInterval(poll, POLL_INTERVAL_MS);
    timeoutTimer = setTimeout(() => {
      // We have given up, and the server's auth.sid session is at or near
      // expiry too. Leaving the window open invites the user to authenticate
      // into a dead flow and collect an "Invalid OAuth state" error, so close
      // it if we still can. Under COOP the reference is severed and this is a
      // no-op, which is why the server also renders a readable page for that
      // case rather than relying on this.
      if (closeWatchTrusted) {
        try {
          popup.close();
          console.log("[auth-debug] closed abandoned popup on timeout");
        } catch (e) {
          /* reference gone, nothing to do */
        }
      }
      settleErr(
        Object.assign(new Error("Sign-in timed out."), {
          code: "AUTH_TIMEOUT",
        }),
        "timeout",
      );
    }, timeoutMs);
  });
}
